import { useState, useEffect, type ComponentType } from 'react'
import { useParams } from 'react-router-dom'
import { BookDemoModal } from '../components/BookDemoModal'
import { Box, Container, Typography, Grid, Chip, Card, CardContent, Stack, Divider, CircularProgress, Alert, InputBase, IconButton, Slider } from '@mui/material'
import type { SvgIconProps } from '@mui/material'
import { FancyGoldButton } from '../components/FancyGoldButton'
import { FancyLightButton } from '../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SearchIcon from '@mui/icons-material/Search'
import { COLOR_PRIMARY, NAVBAR_BG, DARK_700, COLOR_LIVE, BG_LIGHT, COLOR_QUATERNARY, COLOR_AMBER, DARK_800 } from '../theme'
import { buildApiUrl, CONTRACT_NUM, ALL_NUTS, loginpage, registerpage } from '../common/common'
import { WaveDivider } from '../components/WaveDivider'
import PageMeta from '../components/PageMeta'
import { UKRegionPicker } from '../components/UKRegionPicker'
import { PaginationBar } from '../components/PaginationBar'
import posthog, { isPosthogEnabled } from '../posthog'

export interface NoticeFeedFeature {
    Icon: ComponentType<SvgIconProps>
    title: string
    body: string
}

export interface NoticeFeedConfig {
    typeint: 1 | 2 | 3
    showDeadline: boolean
    basePath: string
    noun: string
    metaTitle: string
    metaDescription: string
    keywordMetaDescription: (keyword: string) => string
    heroHighlight: string
    heroRest: string
    heroSub: string
    searchPlaceholder: string
    listTitleAll: string
    featuresTitle: string
    features: NoticeFeedFeature[]
}

interface CpvItem {
    stub: string
    zeros: string
    code: string
    desc: string
    color: string
    division: string
}

interface CpvGroup {
    primarycode: CpvItem
    primarybreakdown?: CpvItem[]
    subgroups: Array<{
        division: string
        desc: string
        color: string
        items: CpvItem[]
    }>
}

interface NoticeResult {
    reftitleshort: string
    publisher: string
    publishermeta: { name?: string } | null
    valuemin: string | number | null
    valuemax: string | number | null
    cpvprimaryhtml: string
    cpvgroup?: CpvGroup
    closingdate: string
    closingdatestring: string
    publisheddatestring: string
    origsite: string
    location: string
    href: string
    summaryshort?: string
    flagcodelist?: string[]
    durationtext?: string
    startdatebestguesstext?: string
    enddatebestguesstext?: string
    isframework?: boolean | number
    isdps?: boolean | number
}

function parseNumericValue(v: string | number | null): number {
    if (v == null) return 0
    return Number(String(v).replace(/[£,\s]/g, '')) || 0
}

function formatValue(min: string | number | null, max: string | number | null): string {
    const v = parseNumericValue(max) || parseNumericValue(min)
    if (!v) return 'TBC'
    if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`
    if (v >= 1_000) return `£${Math.round(v / 1_000)}K`
    return `£${v.toLocaleString()}`
}


// CPV division (first 2 digits) → description
const CPV_DIVISIONS: Record<string, string> = {
    '03': 'Agriculture, farming & forestry',
    '09': 'Petroleum, fuel & energy',
    '14': 'Mining & basic metals',
    '15': 'Food, beverages & tobacco',
    '16': 'Agricultural machinery',
    '18': 'Clothing, footwear & luggage',
    '19': 'Leather, textiles, plastics & rubber',
    '22': 'Printed matter',
    '24': 'Chemical products',
    '30': 'Office & computing machinery',
    '31': 'Electrical machinery & equipment',
    '32': 'Radio, TV & communications equipment',
    '33': 'Medical equipment & pharmaceuticals',
    '34': 'Transport equipment',
    '35': 'Security, fire-fighting & defence equipment',
    '37': 'Musical instruments, sport & toys',
    '38': 'Laboratory & precision equipment',
    '39': 'Furniture, furnishings & cleaning',
    '41': 'Collected & purified water',
    '42': 'Industrial machinery',
    '43': 'Mining & construction equipment',
    '44': 'Construction materials & structures',
    '45': 'Construction works',
    '48': 'Software & information systems',
    '50': 'Repair & maintenance services',
    '51': 'Installation services',
    '55': 'Hotel, restaurant & retail services',
    '60': 'Transport services',
    '63': 'Auxiliary transport & travel services',
    '64': 'Postal & telecommunications',
    '65': 'Public utilities',
    '66': 'Financial & insurance services',
    '70': 'Real estate services',
    '71': 'Architectural & engineering services',
    '72': 'IT services',
    '73': 'Research & development',
    '75': 'Administration, defence & social security',
    '76': 'Oil & gas industry services',
    '77': 'Agricultural & horticultural services',
    '79': 'Business services',
    '80': 'Education & training',
    '85': 'Health & social work',
    '90': 'Environmental & refuse services',
    '92': 'Recreational, cultural & sporting services',
    '98': 'Other community & personal services',
    '99': 'Extraterritorial organisations',
}



function CpvBadge({ cpvitem }: { cpvitem?: CpvItem }) {
    if (!cpvitem) return null
    const { color, division, stub, zeros, desc } = cpvitem
    const divDesc = CPV_DIVISIONS[division]
    return (
        <Box sx={{
            display: 'inline-flex',
            border: '1px solid rgba(0,0,0,0.15)',
            borderLeft: `3px solid ${color}`,
            borderRadius: '6px',
            overflow: 'hidden',
        }}>
            {/* Left: full CPV code */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                px: 1.25, py: 0.75,
                backgroundColor: color,
                borderRight: '1px solid rgba(0,0,0,0.1)',
                flexShrink: 0,
            }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '1px' }}>
                    <Box component="span" sx={{ fontSize: '12px', fontWeight: 800, color: 'black', lineHeight: 1 }}>
                        {stub}
                    </Box>
                    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: 'black', lineHeight: 1, opacity: 0.6 }}>
                        {zeros}
                    </Box>
                </Box>
            </Box>
            {/* Right: primary category (top) + specific description (bottom) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', px: 1, py: 0.5, justifyContent: 'center', gap: 0.2 }}>
                {divDesc && (
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'black', lineHeight: 1.3 }}>
                        {divDesc}
                    </Typography>
                )}
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'black', lineHeight: 1.3 }}>
                    {desc}
                </Typography>
            </Box>
        </Box>
    )
}

function timeUntilDeadline(closingDate: string): string {
    const diff = new Date(closingDate).getTime() - Date.now()
    if (diff <= 0) return 'Closed'
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24))
    if (days === 1) return '1 day'
    if (days < 7) return `${days} days`
    const weeks = Math.round(days / 7)
    if (weeks === 1) return '1 week'
    if (weeks < 52) return `${weeks} weeks`
    const years = Math.round(weeks / 52)
    return `${years} ${years === 1 ? 'year' : 'years'}`
}

function Highlight({ text }: { text: string }) {
    const parts = text.split(/(<em>.*?<\/em>)/g)
    return (
        <>
            {parts.map((part, i) =>
                part.startsWith('<em>') ? (
                    <Typography key={i} component="span" sx={{ color: COLOR_PRIMARY, fontWeight: 700, fontSize: 'inherit', pr: 0.25 }}>
                        {part.slice(4, -5)}
                    </Typography>
                ) : part.split(' ').map((word, j) => (
                    <Typography key={`${i}-${j}`} component="span" sx={{ fontSize: 'inherit', pr: 0.25 }}>
                        {j > 0 ? ' ' : ''}{word}
                    </Typography>
                ))
            )}
        </>
    )
}

function stripLabel(s: string): string {
    // "Closing : 17/4/2026" → "17/4/2026"
    return s.replace(/^[^:]+:\s*/, '').trim()
}

function parseDaysAgo(s: string): { label: string; days: number } {
    const raw = stripLabel(s)
    const [d, m, y] = raw.split('/').map(Number)
    if (!d || !m || !y) return { label: raw, days: 999 }
    const pub = new Date(y, m - 1, d)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    pub.setHours(0, 0, 0, 0)
    const days = Math.round((today.getTime() - pub.getTime()) / 86_400_000)
    if (days === 0) return { label: 'Today', days: 0 }
    if (days === 1) return { label: 'Yesterday', days: 1 }
    if (days > 1) return { label: `${days} days ago`, days }
    return { label: raw, days: 999 }
}

function RelativeDateBadge({ dateString }: { dateString: string }) {
    const { label, days } = parseDaysAgo(dateString)
    let bg: string, color: string
    if (days === 0) {
        bg = `${COLOR_LIVE}22`; color = '#3a9e22'
    } else if (days === 1) {
        bg = `${COLOR_PRIMARY}22`; color = COLOR_AMBER
    } else if (days <= 3) {
        bg = 'rgba(0,0,0,0.05)'; color = 'text.secondary' as string
    } else {
        bg = 'transparent'; color = 'text.disabled' as string
    }
    return (
        <>

            <Box sx={{
                display: 'inline-block',
                px: 1, py: 0.25,
                borderRadius: 1,
                backgroundColor: bg,
                fontSize: '12px',
                fontWeight: days <= 1 ? 700 : 500,
                color,
                whiteSpace: 'nowrap',
            }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ px: 1 }}>Published</Typography>
                <Typography variant="caption" fontWeight={700} >
                    {label}
                </Typography>
            </Box>
        </>
    )
}

// Source data uses ancient sentinel dates (year 0001) for "no deadline"
function hasValidDate(s?: string): boolean {
    if (!s) return false
    const d = new Date(s)
    return !isNaN(d.getTime()) && d.getFullYear() >= 2010
}

function NoticeCard({ notice, showDeadline }: { notice: NoticeResult; showDeadline: boolean }) {
    const cpvColor = notice.cpvgroup?.primarycode?.color ?? '#78909c'
    return (
        <Card variant="outlined" sx={{
            position: 'relative',
            borderRadius: 2,
            border: `1px solid #9C97B271`,
            borderLeft: `4px solid ${cpvColor}`,
            overflow: 'hidden',
            transition: 'box-shadow 0.2s ease',
            '&:hover': {
                boxShadow: '0 2px 16px rgba(0,0,0,0.09)',
            },
        }}>
            {/* Title + meta row */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.35 }}>
                    <Highlight text={notice.reftitleshort} />
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                    <RelativeDateBadge dateString={notice.publisheddatestring} />
                    {showDeadline && hasValidDate(notice.closingdate) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>Deadline</Typography>
                            <Typography variant="caption" fontWeight={700} sx={{ color: COLOR_AMBER }}>
                                {timeUntilDeadline(notice.closingdate)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ px: 1 }}>
                                {new Date(notice.closingdate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </Typography>
                        </Box>
                    )}

                </Box>
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', px: 2.5, py: 2, gap: 3 }}>
                {/* Left: metadata */}
                <Box sx={{ flex: '0 0 38%', display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={800} sx={{ lineHeight: 1.3, fontSize: '15px' }}>
                        <Highlight text={notice.publisher} />
                    </Typography>

                    <CpvBadge cpvitem={notice.cpvgroup?.primarycode ?? undefined} />

                    {notice.location && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ flexShrink: 0 }}>Location</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                                {notice.flagcodelist?.map(code => (
                                    <Box key={code} sx={{ borderRadius: '3px', overflow: 'hidden', lineHeight: 0, flexShrink: 0 }}>
                                        <img src={`/flag-${code}.svg`} alt={code} width={20} height={13} style={{ display: 'block' }} />
                                    </Box>
                                ))}
                                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{notice.location}</Typography>
                            </Box>
                        </Box>
                    )}

                    {(parseNumericValue(notice.valuemax) > 0 || parseNumericValue(notice.valuemin) > 0) ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Value</Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: COLOR_QUATERNARY }}>
                                {formatValue(notice.valuemin, notice.valuemax)}
                            </Typography>
                        </Box>
                    ) : null}

                    {notice.durationtext && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Duration</Typography>
                            <Typography variant="body2" fontWeight={600}>{notice.durationtext}</Typography>
                        </Box>
                    )}

                    {(notice.startdatebestguesstext || notice.enddatebestguesstext) && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Contract</Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {notice.startdatebestguesstext ?? '?'} → {notice.enddatebestguesstext ?? '?'}
                            </Typography>
                        </Box>
                    )}
                    {(notice.isframework || notice.isdps) && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {notice.isframework && (
                                <Box sx={{ display: 'inline-block', px: 0.75, py: 0.15, borderRadius: 0.75, fontSize: '14px', fontWeight: 700, backgroundColor: `${COLOR_PRIMARY}15`, color: COLOR_PRIMARY, border: `1px solid ${COLOR_PRIMARY}40` }}>Framework</Box>
                            )}
                            {notice.isdps && (
                                <Box sx={{ display: 'inline-block', px: 0.75, py: 0.15, borderRadius: 0.75, fontSize: '14px', fontWeight: 700, backgroundColor: 'rgba(49,151,149,0.12)', color: '#2c7a7b', border: '1px solid rgba(49,151,149,0.3)' }}>DPS</Box>
                            )}
                        </Box>
                    )}
                </Box>

                <Divider orientation="vertical" flexItem />

                {/* Right: description + view */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    {notice.summaryshort ? (
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75, fontSize: '13px' }}>
                            <Highlight text={notice.summaryshort} />
                        </Typography>
                    ) : (
                        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '13px' }}>
                            No description available
                        </Typography>
                    )}
                    <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                        <FancyLightButton
                            onClick={() => {
                                if (isPosthogEnabled) {
                                    posthog.capture('notice_login_clicked')
                                }
                                window.location.href = registerpage
                            }}
                            color={COLOR_PRIMARY}
                            endIcon={<ArrowForwardIcon />}
                        >
                            View
                        </FancyLightButton>
                    </Box>
                </Box>
            </Box>
        </Card>
    )
}

const VALUE_STEPS = ['0', '10', '100', '1k', '10k', '100k', '1mil', '10mil', '100mil', '1bil']
const VALUE_LABELS = ['£0', '£10', '£100', '£1K', '£10K', '£100K', '£1M', '£10M', '£100M', 'No limit']
const formatValueLabel = (i: number) => VALUE_LABELS[i] ?? ''

const titleCase = (s: string) =>
    s.split(' ').map(w => w ? w[0].toUpperCase() + w.slice(1) : w).join(' ')

export function NoticeFeedPage({ cfg }: { cfg: NoticeFeedConfig }) {
    const { keyword } = useParams<{ keyword?: string }>()
    const initialQuery = keyword ? decodeURIComponent(keyword).replace(/-/g, ' ') : ''

    const [demoOpen, setDemoOpen] = useState(false)
    const [notices, setNotices] = useState<NoticeResult[]>([])
    const [total, setTotal] = useState<number | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchInput, setSearchInput] = useState(initialQuery)
    const [activeQuery, setActiveQuery] = useState(initialQuery)
    const [page, setPage] = useState(1)
    const [orderBy, setOrderBy] = useState('pd_desc')
    const [sliderValue, setSliderValue] = useState<[number, number]>([0, 9])
    const [committedRange, setCommittedRange] = useState<[number, number]>([0, 9])
    const [nuts, setNuts] = useState<string[]>([...ALL_NUTS])
    const [regionCounts, setRegionCounts] = useState<Record<string, number>>({})
    const [globalCounts, setGlobalCounts] = useState<Record<string, number>>({})
    const [cpvColors, setCpvColors] = useState<Record<string, string>>({})

    // Fetch per-region counts whenever query or value range changes
    useEffect(() => {
        const today = new Date()
        const fmt = (d: Date) => d.toISOString().slice(0, 10).replace(/-/g, '')
        const oneYearAgo = new Date(today)
        oneYearAgo.setFullYear(today.getFullYear() - 1)
        const mi = VALUE_STEPS[committedRange[0]]
        const ma = committedRange[1] === 9 ? 'max' : VALUE_STEPS[committedRange[1]]
        const fields = JSON.stringify(['pu', 'lo', 'su', 'cp', 'ti', 'aw'])
        const params = new URLSearchParams({
            r: Date.now().toString(),
            q: activeQuery,
            sd: fmt(oneYearAgo),
            ed: fmt(today),
            mi,
            ma,
            f: fields,
            ti: String(cfg.typeint),
        })
        const base = window.location.hostname.includes('localhost')
            ? 'http://localhost:9999'
            : 'https://api.contractfinderpro.com'
        fetch(`${base}/api/count?${params}`)
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (data?.success && data.nutsref) {
                    const NUTS_TO_SLUG: Record<string, string> = {
                        'UKC': 'north-east-england',
                        'UKD': 'north-west-england',
                        'UKE': 'yorkshire-and-the-humber-england',
                        'UKF': 'east-midlands-england',
                        'UKG': 'west-midlands-england',
                        'UKH': 'east-of-england',
                        'UKI': 'greater-london-england',
                        'UKJ': 'south-east-england',
                        'UKK': 'south-west-england',
                        'UKL': 'wales',
                        'UKM': 'scotland',
                        'UKN': 'northern-ireland',
                        'IE': 'ireland',
                    }
                    const mapped: Record<string, number> = {}
                    for (const [code, val] of Object.entries(data.nutsref as Record<string, string>)) {
                        const slug = NUTS_TO_SLUG[code]
                        if (slug) mapped[slug] = parseInt(String(val).replace(/,/g, ''), 10)
                    }
                    setRegionCounts(mapped)
                    const global: Record<string, number> = {}
                    for (const key of ['UK', 'EU', 'OS']) {
                        const val = (data.nutsref as Record<string, string>)[key]
                        if (val) global[key] = parseInt(String(val).replace(/,/g, ''), 10)
                    }
                    setGlobalCounts(global)
                }
            })
            .catch(() => { /* silent */ })
    }, [activeQuery, committedRange, cfg.typeint])

    useEffect(() => {
        setLoading(true)
        setError(null)
        const mi = VALUE_STEPS[committedRange[0]]
        const ma = committedRange[1] === 9 ? 'max' : VALUE_STEPS[committedRange[1]]
        fetch(buildApiUrl(activeQuery, page, orderBy, mi, ma, nuts.length > 0 ? nuts : ALL_NUTS, cfg.typeint))
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                return res.json()
            })
            .then(data => {
                setNotices(data.results ?? [])
                setTotal(data.total ?? null)
                const colorMap: Record<string, string> = {}
                for (const sg of (data.subgroups ?? []) as Array<{ division: string; color: string }>) {
                    if (sg.division && sg.color) colorMap[sg.division] = sg.color
                }
                if (Object.keys(colorMap).length > 0) setCpvColors(colorMap)
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false))
    }, [activeQuery, page, orderBy, committedRange, nuts, cfg.typeint])

    const handleSearch = () => {
        const query = searchInput.trim()
        if (isPosthogEnabled) {
            posthog.capture('notice_search_submitted', {
                feed_type: cfg.typeint,
                query_length: query.length,
            })
        }
        setPage(1)
        setActiveQuery(query)
    }

    const totalPages = total != null && notices.length > 0 ? Math.ceil(total / notices.length) : null

    // Keyword landing pages are distinct SEO surfaces: each gets its own title, description
    // and self-referencing canonical (canonicalising to the base would tell Google to fold them)
    const displayKeyword = initialQuery ? titleCase(initialQuery) : ''
    const metaTitle = displayKeyword ? `${displayKeyword} ${cfg.noun}` : cfg.metaTitle
    const metaDescription = displayKeyword ? cfg.keywordMetaDescription(displayKeyword) : cfg.metaDescription
    const canonical = keyword ? `${cfg.basePath}/${encodeURIComponent(keyword)}` : cfg.basePath

    return (
        <Box>
            <PageMeta
                title={metaTitle}
                description={metaDescription}
                canonical={canonical}
            />
            {/* Hero */}
            <Box sx={{
                background: `linear-gradient(135deg, ${NAVBAR_BG} 0%, ${DARK_700} 100%)`,
                pt: { xs: 4, md: 6 },
                pb: { xs: 3, md: 4 },
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Chip
                                label="Updated in real time"
                                size="small"
                                sx={{ mb: 3, backgroundColor: 'rgba(70,145,39,0.2)', color: COLOR_LIVE, border: '1px solid rgba(70,145,39,0.4)', fontWeight: 600 }}
                                icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: COLOR_LIVE, ml: '10px !important', boxShadow: `0 0 4px ${COLOR_LIVE}` }} />}
                            />
                            <Typography variant="h1" sx={{ color: 'white', fontSize: { xs: '36px', md: '48px' }, mb: 2 }}>
                                <Box component="span" sx={{ color: COLOR_PRIMARY, mr: 1 }}>{cfg.heroHighlight}</Box> {cfg.heroRest}<br />

                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '17px', mb: 4, lineHeight: 1.7 }}>
                                {cfg.heroSub}
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                                <FancyGoldButton to="/register" color={COLOR_PRIMARY} endIcon={<ArrowForwardIcon />}>
                                    Join free for 14 days
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)} color={COLOR_PRIMARY}>
                                    Book a Demo
                                </FancyLightButton>
                            </Stack>

                            {/* Search bar */}
                            <Box
                                component="form"
                                onSubmit={e => { e.preventDefault(); handleSearch() }}
                                sx={{
                                    display: 'flex', alignItems: 'center',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: 2,
                                    px: 0.5, py: 0.5,
                                    mb: 3,
                                    '&:focus-within': { borderColor: COLOR_PRIMARY, backgroundColor: 'rgba(255,255,255,0.14)' },
                                    transition: 'all 0.2s',
                                }}
                            >
                                <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)', mr: 1.5, flexShrink: 0, ml: 1 }} />
                                <InputBase
                                    fullWidth
                                    placeholder={cfg.searchPlaceholder}
                                    value={searchInput}
                                    onChange={e => setSearchInput(e.target.value)}
                                    sx={{ fontSize: '15px', color: 'white', '& ::placeholder': { color: 'rgba(255,255,255,0.45)' } }}
                                />
                                {activeQuery && (
                                    <IconButton size="small" onClick={() => { setSearchInput(''); setActiveQuery('') }} sx={{ mr: 0.5, color: 'rgba(255,255,255,0.5)' }}>
                                        <Box component="span" sx={{ fontSize: '18px', lineHeight: 1 }}>×</Box>
                                    </IconButton>
                                )}
                                <Box
                                    component="button"
                                    type="submit"
                                    sx={{
                                        flexShrink: 0, ml: 1,
                                        backgroundColor: COLOR_PRIMARY, color: 'black',
                                        border: 'none', borderRadius: 1.5,
                                        px: 2.5, py: 1,
                                        fontSize: '14px', fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'opacity 0.2s',
                                        '&:hover': { opacity: 0.88 },
                                    }}
                                >
                                    Search
                                </Box>
                            </Box>

                            {/* Contract value slider */}
                            <Box sx={{ px: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>Contract Value</Typography>
                                    <Typography variant="body2" sx={{ color: COLOR_PRIMARY, fontWeight: 700 }}>
                                        {formatValueLabel(sliderValue[0])} — {formatValueLabel(sliderValue[1])}
                                    </Typography>
                                </Box>
                                <Slider
                                    value={sliderValue}
                                    onChange={(_, v) => setSliderValue(v as [number, number])}
                                    onChangeCommitted={(_, v) => { setPage(1); setCommittedRange(v as [number, number]) }}
                                    min={0} max={9} step={1}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={formatValueLabel}
                                    marks={VALUE_LABELS.map((label, i) => ({ value: i, label }))}
                                    sx={{
                                        color: COLOR_PRIMARY,
                                        '& .MuiSlider-markLabel': { fontSize: '11px', color: 'rgba(255,255,255,0.4)' },
                                        '& .MuiSlider-markLabel[data-index="0"]': { transform: 'translateX(0%)' },
                                        '& .MuiSlider-markLabel[data-index="9"]': { transform: 'translateX(-100%)' },
                                        '& .MuiSlider-rail': { backgroundColor: 'rgba(255,255,255,0.2)' },
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Box sx={{
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 2,
                                px: 2,
                                py: 1
                            }}>

                                <Box >
                                    <UKRegionPicker
                                        selected={nuts}
                                        onChange={n => { setPage(1); setNuts(n) }}
                                        counts={regionCounts}
                                        globalCounts={globalCounts}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* Results list */}
            <Box sx={{ backgroundColor: BG_LIGHT, py: { xs: 4, md: 4 } }}>
                <Container maxWidth="lg">
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="h3">
                            {activeQuery ? `${cfg.noun} for "${activeQuery}"` : cfg.listTitleAll}
                        </Typography>
                        {total != null ? (
                            <Typography variant="h5" sx={{ color: `${COLOR_PRIMARY}` }}>
                                Sign up to search, filter and save
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {`A sample from today's live feed. Sign up to search ${CONTRACT_NUM} live and historical contracts.`}
                            </Typography>
                        )}
                    </Box>

                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: COLOR_PRIMARY }} />
                        </Box>
                    )}

                    {error && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            Live feed temporarily unavailable. Please try again shortly.
                        </Alert>
                    )}

                    {!loading && totalPages != null && totalPages > 1 && (
                        <Box sx={{ mb: 2 }}>
                            <PaginationBar
                                page={page}
                                totalPages={totalPages}
                                loading={loading}
                                onPageChange={setPage}
                                showOrderBy={true}
                                liveCount={total}
                            />
                        </Box>
                    )}

                    {!loading && !error && notices.length > 0 && (
                        <Stack spacing={2}>
                            {notices.map((notice, i) => (
                                <NoticeCard key={notice.href ?? i} notice={notice} showDeadline={cfg.showDeadline} />
                            ))}
                        </Stack>
                    )}

                    {totalPages != null && totalPages > 1 && (
                        <Box sx={{ mt: 3 }}>
                            <PaginationBar
                                page={page}
                                totalPages={totalPages}
                                loading={loading}
                                onPageChange={setPage}
                                showOrderBy={false}
                            />
                        </Box>
                    )}

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <FancyGoldButton to="/pricing" color={COLOR_PRIMARY} endIcon={<ArrowForwardIcon />}>
                            Join free for 14 days
                        </FancyGoldButton>
                    </Box>
                </Container>
            </Box >

            {/* Features */}
            < Box sx={{ backgroundColor: 'white', py: { xs: 4, md: 6 } }
            }>
                <Container maxWidth="lg">
                    <Typography variant="h3" sx={{ textAlign: 'center', mb: 7 }}>{cfg.featuresTitle}</Typography>
                    <Grid container spacing={4}>
                        {cfg.features.map(feature => (
                            <Grid item xs={12} sm={6} key={feature.title}>
                                <Card sx={{ p: 1 }}>
                                    <CardContent>
                                        <Box sx={{
                                            width: 44, height: 44, borderRadius: 2,
                                            backgroundColor: 'rgba(244,182,50,0.12)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            mb: 2,
                                        }}>
                                            <feature.Icon sx={{ color: COLOR_PRIMARY, fontSize: 36 }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ mb: 1 }}>{feature.title}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{feature.body}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box >
            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box >
    )
}
